import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel } from '../../../../constants';
import { getMaterialStateColor } from '../../../../constants/colors';
import { useMachinesWithLastLocationsQuery } from '../../../../hooks/queries/useMachinesQuery';
import { useToolsWithLastLocationsQuery } from '../../../../hooks/queries/useToolsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { MachineWithLastLocation } from '../../../../services/apiSLTP/materials/machines';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatDate, formatDateToDDMMYYYY } from '../../../../utils/dates';
import CategoryCell, { CategoryType } from '../../../cells/CategoryCell';
import StateCell from '../../../cells/StateCell';
import GenericAgGridTable from '../../GenericAgGridTable';

interface PlanningMaterialPageProps {
    currentDate: Date;
}

const PlanningMaterialPage: React.FC<PlanningMaterialPageProps> = ({ currentDate }) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const {
        data: dataMachinesWithLocations,
        isSuccess: isMachinesSuccess,
        isLoading: isMachinesLoading,
    } = useMachinesWithLastLocationsQuery({ date: currentDate });
    const {
        data: dataToolsWithLocations,
        isSuccess: isToolsSuccess,
        isLoading: isToolsLoading,
    } = useToolsWithLastLocationsQuery({ date: currentDate });

    interface MaterialItem extends MachineWithLastLocation {
        color: string;
        category: string;
    }

    const columnDefs: ColDef<MaterialItem>[] = [
        {
            headerName: capitalizeFirstLetter(t('category')),
            valueGetter: (params) => t(params.data?.category || ''),
            cellRenderer: (params: ICellRendererParams<MaterialItem>) => {
                if (!params.data) return null;
                return <CategoryCell category={params.data?.category as CategoryType} />;
            },
            colId: 'category',
        },
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'type',
            colId: 'type',
        },
        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteName',
            initialWidth: 400,
            colId: 'site',
        },
        {
            headerName: capitalizeFirstLetter(t('conduc')),
            valueGetter: (params) => params.data?.conducUsername,
            cellRenderer: (params: ICellRendererParams<MaterialItem>) => {
                if (!params.data) return null;
                return (
                    <StateCell
                        stateColor={params.data?.conducColor}
                        text={params.data?.conducUsername}
                        showDot={false}
                        style={{ width: 60, justifyContent: 'center' }}
                    />
                );
            },
            width: 150,
            colId: 'conduc',
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => t(getMaterialStateLabel(params.data?.latestState)),
            cellRenderer: (params: ICellRendererParams<MaterialItem>) => {
                if (!params.data) return null;
                return (
                    <StateCell
                        stateColor={getMaterialStateColor(params.data?.latestState)}
                        text={t(getMaterialStateLabel(params.data?.latestState))}
                    />
                );
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('date')),
            field: 'latestDate',
            valueGetter: (params) => {
                const dateStr = params.data?.latestDate;
                return dateStr ? new Date(dateStr) : null;
            },
            valueFormatter: (params) => {
                const date = params.value;
                return date instanceof Date ? formatDateToDDMMYYYY(date, '/') : '';
            },
            filter: 'agDateColumnFilter',
            sortable: true,
            colId: 'date',
        },
    ];

    return (
        <>
            <GenericAgGridTable
                rowData={
                    dataMachinesWithLocations
                        ?.sort((a, b) => {
                            return a.designation.localeCompare(b.designation);
                        })
                        .map((value) => ({ ...value, color: 'blue', category: 'machine' as CategoryType }))
                        .concat(
                            dataToolsWithLocations
                                ?.sort((a, b) => a.designation.localeCompare(b.designation))
                                .map((value) => ({ ...value, color: 'pink', category: 'tool' as CategoryType })) || []
                        ) || []
                }
                columnDefs={columnDefs}
                loading={isMachinesLoading || isToolsLoading}
                exportCsvFileName={`planning-material-${formatDate(currentDate)}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
        </>
    );
};

export default PlanningMaterialPage;
